import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { authService } from "@/services/auth.service";
import { endpoints } from "@/config/endpoints";
import { ArrowLeft, Download } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface Site {
  _id: string;
  title: string;
  location: string;
  siteType: string;
  siteManager: {
    id: string;
    name: string;
    phone: string;
  };
  tools?: Array<{
    name: string;
    category: string;
    quantity: number;
    unit: string;
    status?: string;
    _id?: string;
    damageDetails?: {
      quantity: number;
      imageUrl?: string;
    };
  }>;
  phases?: Array<{
    name: string;
    materials: Array<{
      name: string;
      quantity?: number;
      unit?: string;
    }>;
  }>;
}

interface Supplier {
  _id: string;
  name: string;
  email: string;
  phone: string;
  contactPerson?: string;
}

interface ApprovalHistory {
  approver: string;
  status: string;
  timestamp: string;
  comments?: string;
  suppliers?: Supplier[];
  supplier?: Supplier;
  rejectionImageUrl?: string;
}

interface Request {
  _id: string;
  requestId: string;
  itemName: string;
  quantity: string;
  status: string;
  deliveryStatus: string;
  phase: string;
  location: string;
  createdAt: string;
  createdBy: {
    name: string;
    email: string;
  };
  product: {
    title: string;
    type: string;
  };
  approvalFlow: {
    stages: ApprovalStage[];
  };
  approvalHistory: ApprovalHistory[];
  currentApprover: string;
  type: string;
  orderId: string;
  siteId: string;
  deliveryImages?: Array<{
    url: string;
    uploadedAt: string;
    timestamp: string;
    _id: string;
  }>;
  suppliers?: Supplier[];
  isDeliveryCorrect: boolean;
  isQualityCorrect: boolean;
  isQuantityCorrect: boolean;
  verification?: {
    isVerified: boolean;
    verificationImages: Array<{
      url: string;
      uploadedAt: string;
      timestamp: string;
      _id: string;
    }>;
  };
  settledAt?: string;
  settledBy?: { name: string; email: string; phone: string; _id: string };
  multipleSuppliers: boolean;
  description?: string;
  updatedAt: string;
}

interface ApprovalStage {
  role: string;
  status: string;
  timestamp?: string;
}

const getStatusColor = (status: string) => {
  if (status === "approved") return "bg-green-500";
  if (status === "pending") return "bg-yellow-500";
  return "bg-red-500";
};

const getStatusDisplay = (status: string) => {
  if (!status) return "N/A";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getToolStatusColor = (status?: string) => {
  if (!status) return "bg-gray-100 text-gray-800";
  switch (status.toLowerCase()) {
    case "available":
      return "bg-green-100 text-green-800";
    case "in use":
      return "bg-blue-100 text-blue-800";
    case "maintenance":
      return "bg-yellow-100 text-yellow-800";
    case "damaged":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const SiteDetails = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { token } = authService.getStoredAuth();
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(
    null
  );
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleDownloadPDF = async () => {
    if (!contentRef.current || !site) return;

    try {
      setIsGeneratingPDF(true);
      toast.info("Generating PDF...");

      // Temporarily expand all requests for PDF generation
      const allRequestIds = siteRequests.map((request) => request._id);
      setExpandedRequestId(null); // Reset to ensure clean state

      // Wait for the DOM to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Create canvas from the content
      const canvas = await html2canvas(contentRef.current, {
        scale: 2, // Higher scale for better quality
        useCORS: true, // Enable CORS for images
        logging: false,
        windowWidth: contentRef.current.scrollWidth,
        windowHeight: contentRef.current.scrollHeight,
      });

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", "a4");
      let position = 0;

      // Add the image to the PDF
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 1.0),
        "JPEG",
        0,
        position,
        imgWidth,
        imgHeight
      );

      // If content is longer than one page, add more pages
      while (position - imgHeight < -297) {
        // 297mm is A4 height
        position = position - 297;
        pdf.addPage();
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 1.0),
          "JPEG",
          0,
          position,
          imgWidth,
          imgHeight
        );
      }

      // Save the PDF
      pdf.save(
        `site-details-${site.title}-${
          new Date().toISOString().split("T")[0]
        }.pdf`
      );
      toast.success("PDF downloaded successfully!");

      // Reset expanded requests
      setExpandedRequestId(null);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Fetch site details
  const {
    data: site,
    isLoading: siteLoading,
    error: siteError,
  } = useQuery<Site>({
    queryKey: ["site", siteId],
    queryFn: async () => {
      const response = await fetch(`${endpoints.sites.site(siteId!)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch site details");
      }
      return response.json();
    },
  });

  // Fetch all requests and filter by site
  const { data: allRequests, isLoading: requestsLoading } = useQuery<Request[]>(
    {
      queryKey: ["allRequests"],
      queryFn: async () => {
        const response = await fetch(`${endpoints.requests.list}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch requests");
        }
        return response.json();
      },
    }
  );

  // Filter requests by site ID
  const siteRequests =
    allRequests?.filter((request) => request.siteId === siteId) || [];

  if (siteError) {
    toast.error("Failed to load site details");
    return null;
  }

  return (
    <DashboardLayout title="Site Details">
      <div className="space-y-6" ref={contentRef}>
        <div className="flex justify-between items-center mb-4">
          <Button variant="outline" onClick={() => navigate("/sites")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Sites
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
          >
            <Download className="mr-2 h-4 w-4" />
            {isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Site Details</CardTitle>
          </CardHeader>
          <CardContent>
            {siteLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-4 w-[300px]" />
                <Skeleton className="h-4 w-[250px]" />
              </div>
            ) : site ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Title</h3>
                  <p className="mt-1 text-sm">{site.title}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Location
                  </h3>
                  <p className="mt-1 text-sm">{site.location}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Site Type
                  </h3>
                  <p className="mt-1 text-sm">
                    <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
                      {site.siteType}
                    </span>
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Site Manager
                  </h3>
                  <div className="mt-1 text-sm">
                    <p className="font-medium">
                      {site.siteManager?.name || "N/A"}
                    </p>
                    <p className="text-gray-500">
                      {site.siteManager?.phone || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Tools Section */}
        <Card>
          <CardHeader>
            <CardTitle>Site Tools</CardTitle>
          </CardHeader>
          <CardContent>
            {siteLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-4 w-[300px]" />
              </div>
            ) : site?.tools && site.tools.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {site.tools.map((tool, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50">
                    <h4 className="font-medium text-lg">{tool.name}</h4>
                    <div className="mt-2 space-y-1 text-sm">
                      <p>
                        <span className="text-gray-500">Category:</span>{" "}
                        {tool.category}
                      </p>
                      <p>
                        <span className="text-gray-500">Quantity:</span>{" "}
                        {tool.quantity} {tool.unit}
                      </p>
                      <p>
                        <span className="text-gray-500">Status:</span>{" "}
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${getToolStatusColor(
                            tool.status
                          )}`}
                        >
                          {tool.status
                            ? tool.status.charAt(0).toUpperCase() +
                              tool.status.slice(1)
                            : "Unknown"}
                        </span>
                      </p>
                      {tool.status?.toLowerCase() === "damaged" &&
                        tool.damageDetails && (
                          <div className="mt-2 p-2 bg-red-50 rounded-md">
                            <p className="text-red-700 font-medium">
                              Damage Details:
                            </p>
                            <p className="text-sm text-red-600">
                              Damaged Quantity: {tool.damageDetails.quantity}{" "}
                              {tool.unit}
                            </p>
                            {tool.damageDetails.imageUrl && (
                              <div className="mt-2">
                                <img
                                  src={tool.damageDetails.imageUrl}
                                  alt="Damage evidence"
                                  className="w-full h-32 object-cover rounded-md"
                                />
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                No tools assigned to this site.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Phases Section */}
        <Card>
          <CardHeader>
            <CardTitle>Site Phases</CardTitle>
          </CardHeader>
          <CardContent>
            {siteLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-4 w-[300px]" />
              </div>
            ) : site?.phases && site.phases.length > 0 ? (
              <div className="space-y-4">
                {site.phases.map((phase, index) => (
                  <div key={index} className="p-4 border rounded-lg">
                    <h4 className="font-medium text-lg">{phase.name}</h4>
                    {phase.materials && phase.materials.length > 0 ? (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {phase.materials.map((material, matIndex) => (
                          <div
                            key={matIndex}
                            className="p-3 bg-gray-50 rounded-md"
                          >
                            <p className="text-sm">
                              {material.name || "Unnamed Material"}
                            </p>
                            {material.quantity && (
                              <p className="text-xs text-gray-500 mt-1">
                                Quantity: {material.quantity}{" "}
                                {material.unit || "units"}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mt-2">
                        No materials assigned to this phase.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                No phases defined for this site.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Associated Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {requestsLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-4 border rounded-md">
                    <Skeleton className="h-4 w-[200px] mb-2" />
                    <Skeleton className="h-4 w-[150px]" />
                  </div>
                ))}
              </div>
            ) : siteRequests.length > 0 ? (
              <div className="space-y-4">
                {siteRequests.map((request) => (
                  <div key={request._id} className="p-4 border rounded-md">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-lg">
                            {request.itemName || "N/A"}
                          </h4>
                          <span className="text-xs text-gray-500">
                            PO #{request.orderId || "N/A"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">
                          {request.product?.title || request.itemName || "N/A"}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            request.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : request.status === "pending"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {getStatusDisplay(request.status)}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            request.deliveryStatus === "delivered"
                              ? "bg-green-100 text-green-800"
                              : request.deliveryStatus === "partial"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          Delivery: {getStatusDisplay(request.deliveryStatus)}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                      <div>
                        <span className="text-gray-500">Quantity:</span>{" "}
                        {request.quantity || "N/A"}
                      </div>
                      <div>
                        <span className="text-gray-500">Phase:</span>{" "}
                        {request.phase || "N/A"}
                      </div>
                      <div>
                        <span className="text-gray-500">PO No:</span>{" "}
                        {request.orderId || "N/A"}
                      </div>
                      <div>
                        <span className="text-gray-500">Type:</span>{" "}
                        {(request.type || "N/A").replace(/_/g, " ")}
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Requested by:</span>{" "}
                        {request.createdBy?.name || "Unknown"}
                      </p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-400">
                          {request.createdAt
                            ? new Date(request.createdAt).toLocaleDateString() +
                              " at " +
                              new Date(request.createdAt).toLocaleTimeString()
                            : "N/A"}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full">
                            Awaiting:{" "}
                            {request.currentApprover
                              ? request.currentApprover.replace(/_/g, " ")
                              : "N/A"}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setExpandedRequestId(
                                expandedRequestId === request._id
                                  ? null
                                  : request._id
                              )
                            }
                            className="text-xs"
                          >
                            {expandedRequestId === request._id
                              ? "Hide Details"
                              : "View Details"}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {expandedRequestId === request._id && (
                      <div className="mt-4 space-y-4">
                        <div className="border-t pt-4">
                          <h5 className="font-medium mb-2">Approval Flow</h5>
                          <div className="space-y-2">
                            {request.approvalFlow?.stages?.map(
                              (stage, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-2 text-sm"
                                >
                                  <span
                                    className={`w-2 h-2 rounded-full ${getStatusColor(
                                      stage.status
                                    )}`}
                                  />
                                  <span className="font-medium">
                                    {stage.role?.replace(/_/g, " ") || "N/A"}:
                                  </span>
                                  <span className="capitalize">
                                    {stage.status || "N/A"}
                                  </span>
                                  {stage.timestamp && (
                                    <span className="text-gray-500 text-xs">
                                      (
                                      {new Date(
                                        stage.timestamp
                                      ).toLocaleString()}
                                      )
                                    </span>
                                  )}
                                </div>
                              )
                            ) || (
                              <p className="text-sm text-gray-500">
                                No approval stages found
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <h5 className="font-medium mb-2">Approval History</h5>
                          <div className="space-y-2">
                            {request.approvalHistory?.map((history, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 text-sm"
                              >
                                {/* <img
                                  src={history.rejectionImageUrl}
                                  alt="Rejection Image"
                                  className="w-10 h-10 rounded-full"
                                /> */}
                                <span
                                  className={`w-2 h-2 rounded-full ${getStatusColor(
                                    history.status
                                  )}`}
                                />
                                <span className="font-medium">
                                  {history.approver?.replace(/_/g, " ") ||
                                    "N/A"}
                                  :
                                </span>
                                <span className="capitalize">
                                  {history.status || "N/A"}
                                </span>
                                {history.comments && (
                                  <span className="text-gray-500 text-xs">
                                    ({history.comments})
                                  </span>
                                )}
                                <span className="text-gray-500 text-xs">
                                  (
                                  {history.timestamp
                                    ? new Date(
                                        history.timestamp
                                      ).toLocaleString()
                                    : "N/A"}
                                  )
                                </span>
                              </div>
                            )) || (
                              <p className="text-sm text-gray-500">
                                No approval history found
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Delivery Images Section */}
                        {request.deliveryImages &&
                          request.deliveryImages.length > 0 && (
                            <div className="border-t pt-4">
                              <h5 className="font-medium mb-2">
                                Delivery Images
                              </h5>
                              <div className="grid grid-cols-2 gap-4">
                                {request.deliveryImages.map((image, index) => (
                                  <div key={index} className="relative">
                                    <img
                                      src={image.url}
                                      alt={`Delivery ${index + 1}`}
                                      className="w-full h-48 object-cover rounded-lg"
                                    />
                                    <span className="text-xs text-gray-500 mt-1 block">
                                      Uploaded:{" "}
                                      {new Date(
                                        image.uploadedAt
                                      ).toLocaleString()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Supplier Details Section */}
                        {request.suppliers && request.suppliers.length > 0 && (
                          <div className="border-t pt-4">
                            <h5 className="font-medium mb-2">
                              Supplier Details
                            </h5>
                            <div className="space-y-4">
                              {request.suppliers.map((supplier, index) => (
                                <div
                                  key={index}
                                  className="bg-gray-50 p-3 rounded-lg"
                                >
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <span className="font-medium">Name:</span>{" "}
                                      {supplier.name}
                                    </div>
                                    <div>
                                      <span className="font-medium">
                                        Email:
                                      </span>{" "}
                                      {supplier.email}
                                    </div>
                                    <div>
                                      <span className="font-medium">
                                        Phone:
                                      </span>{" "}
                                      {supplier.phone}
                                    </div>
                                    {supplier.contactPerson && (
                                      <div>
                                        <span className="font-medium">
                                          Contact Person:
                                        </span>{" "}
                                        {supplier.contactPerson}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Verification Status Section */}
                        <div className="border-t pt-4">
                          <h5 className="font-medium mb-2">
                            Verification Status
                          </h5>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium">
                                Delivery Correct:
                              </span>{" "}
                              <span
                                className={
                                  request.isDeliveryCorrect
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {request.isDeliveryCorrect ? "Yes" : "No"}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium">
                                Quality Correct:
                              </span>{" "}
                              <span
                                className={
                                  request.isQualityCorrect
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {request.isQualityCorrect ? "Yes" : "No"}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium">
                                Quantity Correct:
                              </span>{" "}
                              <span
                                className={
                                  request.isQuantityCorrect
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {request.isQuantityCorrect ? "Yes" : "No"}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium">Verified:</span>{" "}
                              <span
                                className={
                                  request.verification?.isVerified
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {request.verification?.isVerified
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Additional Details Section */}
                        <div className="border-t pt-4">
                          <h5 className="font-medium mb-2">
                            Additional Details
                          </h5>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium">Request ID:</span>{" "}
                              {request.requestId}
                            </div>
                            <div>
                              <span className="font-medium">PO No:</span>{" "}
                              {request.orderId}
                            </div>
                            <div>
                              <span className="font-medium">Created At:</span>{" "}
                              {new Date(request.createdAt).toLocaleString()}
                            </div>
                            <div>
                              <span className="font-medium">Updated At:</span>{" "}
                              {new Date(request.updatedAt).toLocaleString()}
                            </div>
                            <div>
                              <span className="font-medium">Settled At:</span>{" "}
                              {request.settledAt
                                ? new Date(request.settledAt).toLocaleString()
                                : "N/A"}
                            </div>
                            <div>
                              <span className="font-medium">Settled By:</span>{" "}
                              {request.settledBy?.name || "N/A"}
                            </div>
                            <div>
                              <span className="font-medium">
                                Multiple Suppliers:
                              </span>{" "}
                              {request.multipleSuppliers ? "Yes" : "No"}
                            </div>
                            <div>
                              <span className="font-medium">Description:</span>{" "}
                              {request.description || "N/A"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                No requests associated with this site.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SiteDetails;
